
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { AppSettings } from '@/types/appSettings';

export const useAppSettings = () => {
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('app_settings')
        .select('*')
        .limit(1)
        .single();
        
      if (error) {
        throw error;
      }
      
      setSettings(data);
    } catch (err) {
      console.error('Error fetching app settings:', err);
      const error = err instanceof Error ? err : new Error('Error fetching app settings');
      setError(error);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);
  
  const refetchSettings = useCallback(async () => {
    await fetchSettings();
  }, [fetchSettings]);
  
  // Extract common settings to use directly
  const appName = settings?.app_name || 'KFC Antilles';
  const logoUrl = settings?.logo_url || '';
  const faviconUrl = settings?.favicon_url || '';
  const gamesAllowedPerDay = settings?.games_allowed_per_day || 3;
  
  // Function to update PWA assets based on settings
  const updatePwaAssets = useCallback(() => {
    if (settings) {
      // Update favicon if provided
      if (settings.favicon_url) {
        const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
        if (favicon) {
          favicon.href = settings.favicon_url;
        }
      }
      
      // Update other PWA assets if needed
      // This could be expanded in the future to update manifest.json etc.
    }
  }, [settings]);
  
  return {
    settings,
    loading,
    error,
    refetchSettings,
    appName,
    logoUrl,
    faviconUrl,
    gamesAllowedPerDay,
    updatePwaAssets
  };
};
